/**
 * Scroll parallax for the main hero (#shopify-section-hero_jVaWmY).
 * The background media moves at a different rate than page scroll, giving
 * a sense of depth. Implemented with JS + transform (not
 * background-attachment: fixed) so it works identically on iOS Safari,
 * which ignores fixed backgrounds. Respects prefers-reduced-motion.
 *
 * The target element is re-queried on every frame rather than cached once,
 * since this theme's section hydration can replace the DOM node after
 * initial load, which would otherwise silently detach a cached reference
 * and make the effect appear to do nothing.
 *
 * Also keeps the hero background video always playing. Mobile browsers can
 * pause a backgrounded <video> (app switch, screen lock, OS power saving,
 * an accidental tap) and won't always resume it automatically even though
 * it has autoplay/loop set, so this re-plays it whenever it stops or the
 * tab becomes visible again. It also retries play() on the visitor's first
 * interaction with the page, since some browsers/devices (Safari's
 * "Never Auto-Play" setting, iOS Low Power Mode, some Android data-saver
 * modes) refuse ALL programmatic autoplay until a user gesture occurs,
 * showing a native "tap to play" affordance on the video in the meantime.
 */
(function () {
  // Match by suffix rather than a hardcoded full id: Shopify renders this
  // section's actual id as "shopify-section-<template-prefix>__hero_jVaWmY"
  // on the live storefront, not the bare "shopify-section-hero_jVaWmY" the
  // theme editor preview uses, so an exact getElementById lookup silently
  // matched nothing and disabled this whole script in production.
  var section = document.querySelector('[id$="hero_jVaWmY"]');
  if (!section) return;

  function getVideo() {
    return section.querySelector('video.hero__media');
  }

  function keepPlaying() {
    var video = getVideo();
    if (video && video.paused) {
      video.play().catch(function () {
        /* Autoplay can still be blocked (e.g. no user interaction yet); ignore. */
      });
    }
  }

  // Re-attach on every call rather than once, for the same reason `update()`
  // below re-queries `.hero__media`: section hydration can swap the node.
  function watchVideo() {
    var video = getVideo();
    if (!video || video.dataset.keepPlayingBound) return;
    video.dataset.keepPlayingBound = 'true';
    video.addEventListener('pause', keepPlaying);
    video.addEventListener('ended', keepPlaying);
  }

  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) keepPlaying();
  });

  // Some browsers/devices (Safari's "Never Auto-Play" setting, iOS Low Power
  // Mode, certain Android data-saver modes) block programmatic autoplay
  // entirely until the visitor interacts with the page, and show a native
  // "tap to play" affordance on the video in the meantime. A single user
  // gesture anywhere on the page is enough to lift that restriction, so
  // retry play() on the first interaction and then stop listening.
  var unlockEvents = ['pointerdown', 'touchstart', 'click', 'keydown'];
  function unlockOnInteraction() {
    keepPlaying();
    unlockEvents.forEach(function (evt) {
      document.removeEventListener(evt, unlockOnInteraction);
    });
  }
  unlockEvents.forEach(function (evt) {
    document.addEventListener(evt, unlockOnInteraction, { passive: true });
  });

  watchVideo();
  keepPlaying();
  window.setInterval(function () {
    watchVideo();
    keepPlaying();
  }, 2000);

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  var factor = 0.9;
  var ticking = false;

  function update() {
    var media = section.querySelector('.hero__media');
    if (media) {
      var rect = section.getBoundingClientRect();
      if (rect.bottom > 0 && rect.top < window.innerHeight) {
        var offset = -rect.top * factor;
        media.style.transform = 'translateY(' + offset + 'px) scale(1.8)';
      }
    }
    ticking = false;
  }

  function onScroll() {
    if (!ticking) {
      window.requestAnimationFrame(update);
      ticking = true;
    }
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  update();
})();
